/* eslint-disable max-lines-per-function */
import { ReactNode, useCallback } from 'react';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { CurrentHealthcareProviderPaymentsConfigurationEnabledQuery } from '@dreryk/egabinet-online-payments-integration-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import {
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   useCopyToClipboardAsync,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { performCqrsQueryFind } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';

import { translationKeys } from '../../../translations';

import {
   hasAnyReceptionServiceWithAdvancePayment,
   performAppointmentDetailsCqrsGet,
} from './AdvancePaymentShareOnlinePaymentLinkToPatientShowModalEventInterceptor.utils';
import { AdvancePaymentAdditionalPaymentForm } from './AdvancePaymentAdditionalPaymentForm';
import { performPaymentOrderRedirectUrlCqrsGet } from './AppointmentAdvancePaymentLinkShowModalEventInterceptor.utils';
import { AdvancePaymentShareOnlinePaymentLinkToPatientActionEventInterceptor } from './AdvancePaymentShareOnlinePaymentLinkToPatientActionEventInterceptor';

type AdvancePaymentShareOnlinePaymentLinkToPatientShowModalEventInterceptorProps = Readonly<{
   appointmentId: AppointmentId | null;
   onlinePaymentId: string;
   requiredAmount: Money;
   children: ReactNode;
}>;

export const AdvancePaymentShareOnlinePaymentLinkToPatientShowModalEventInterceptor = (
   props: AdvancePaymentShareOnlinePaymentLinkToPatientShowModalEventInterceptorProps
): JSX.Element => {
   const { copy } = useCopyToClipboardAsync({
      onSuccess: () => {
         dispatchSnackbarPositiveEvent(translationKeys.linkToPaymentHasBeenCopied);
      },
   });

   const handleCopy = async (): Promise<void> => {
      await copy(async () => performPaymentOrderRedirectUrlCqrsGet(props.onlinePaymentId));
   };

   const shouldShowModal = useCallback(async () => {
      if (isDemo()) {
         return false;
      }
      const advancePaymentEnabled = protectFeature('searchingPrivateMedicalServices', true, false);
      const onlinePaymentsIntegrationExists = await performCqrsQueryFind(
         () => new CurrentHealthcareProviderPaymentsConfigurationEnabledQuery()
      );
      if (!advancePaymentEnabled || !onlinePaymentsIntegrationExists || !props.appointmentId) {
         return false;
      }

      const [appointmentDetails] = await Promise.all([performAppointmentDetailsCqrsGet(props.appointmentId)]);

      if (!hasAnyReceptionServiceWithAdvancePayment(appointmentDetails)) {
         return false;
      }

      return {
         appointmentDetails: appointmentDetails,
      };
   }, [props.appointmentId]);

   return (
      <ShowModalEventInterceptor
         shouldShowModal={shouldShowModal}
         testId={'appointmentAdvancePayment'}
         title={translationKeys.shareLinkToPayment}
         footerItemsHidden
         modalWidth={ModalWidth.NORMAL}
         content={({ resolve, result: { appointmentDetails } }) => {
            return (
               <AdvancePaymentAdditionalPaymentForm
                  appointmentDetails={appointmentDetails}
                  patientPhoneNumber={appointmentDetails.patient.phoneNumber}
                  requiredAmount={props.requiredAmount}
               >
                  {({ handleSubmit, formState, trigger }) => (
                     <>
                        <ValidateFormEventInterceptor trigger={trigger}>
                           <AdvancePaymentShareOnlinePaymentLinkToPatientActionEventInterceptor
                              onlinePaymentId={props.onlinePaymentId}
                              handleSubmit={handleSubmit}
                           >
                              <ModalConfirmButton
                                 testId={'sendLinkUsingSMS'}
                                 label={translationKeys.sendLinkUsingSMS}
                                 onClick={resolve}
                              />
                           </AdvancePaymentShareOnlinePaymentLinkToPatientActionEventInterceptor>
                        </ValidateFormEventInterceptor>

                        <ModalConfirmButton
                           variant={ButtonVariant.SECONDARY}
                           testId={'copy'}
                           label={translationKeys.copyLink}
                           onClick={handleCopy}
                        />
                        <ModalCancelButton
                           label={translationKeys.close}
                           onClick={resolve}
                           shouldConfirmExit={formState.isDirty}
                        />
                        <ModalCloseButton onClick={resolve} shouldConfirmExit={formState.isDirty} />
                     </>
                  )}
               </AdvancePaymentAdditionalPaymentForm>
            );
         }}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
